import {ActivityLog} from '@/features/entity-activity-logs/types'
import {projectRefTypeMapToLabel} from '@/features/projects/utils'
import {ProjectRefType, ProjectType} from '@/features/projects/types'
import {TKey} from '@/translations/i18n'

export interface ProjectLifecycleContext {
    projectRefType: ProjectRefType | null
    projectType: ProjectType | null
}

/**
 * A log entry whose oldData/newData carry different ids is NOT a field-level edit: every project
 * lifecycle operation (renewal / cycle extension / transformation / reopening) goes through
 * `POST projects/:id/renew-transform`, which creates a NEW project row. The backend records that as
 * an `update` where oldData is the SOURCE project and newData is the freshly created one — so a
 * naive field diff reports meaningless noise ("8 fields changed", including id/createdAt/updatedAt).
 */
export const isLifecycleLogEntry = (log: ActivityLog): boolean => {
    const sourceId = log.oldData?.id
    const createdId = log.newData?.id

    return typeof sourceId === 'string' && typeof createdId === 'string' && sourceId !== createdId
}

/**
 * Mirrors the derivation already used by RenewalTooltip (the "previous project" badge in the details
 * header): cyclic projects disambiguate reopening vs cycle extension through the source project's
 * endDate, while continuous ones map straight from projectRefType.
 *
 * Unlike RenewalTooltip — which refetches the previous project — the source project's endDate is read
 * from the log's own oldData snapshot, i.e. its state at the moment of the operation. That is both
 * cheaper (no request per row) and more faithful to an audit log.
 *
 * Returns null when the entry is not a project lifecycle event, so callers fall back to the field diff.
 */
export const getProjectLifecycleLabel = (log: ActivityLog, context?: ProjectLifecycleContext): TKey | null => {
    if (log.entityType !== 'project' || !isLifecycleLogEntry(log) || !context?.projectRefType) {
        return null
    }

    if (context.projectType === 'cyclic') {
        return log.oldData?.endDate ? 'projects:reopen' : 'projects:extension'
    }

    return projectRefTypeMapToLabel[context.projectRefType]
}
